import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';

import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import PromptMessage from '@/components/UI/PromptMessage';
import { useLogin } from '@/hooks/useLogin';
import BffApiService from '@/shared/bffApiService';
import PasswordFieldTransparent from '@/components/shared/form/PasswordFieldTransparent';
import TextInputFieldTransparent from '@/components/shared/form/TextInputFieldTransparent';
import { LoginFlowVariant } from '@/store/authentication/AuthenticationStore';

const ForgotPasswordSmsChangeForm = ({
  mobile,
  onSuccessCallback,
}: {
  mobile: { mobile: string; mobilePrefix: string };
  onSuccessCallback: () => void;
}) => {
  const intl = useIntl();
  const { request, loading, error } = useBffApiRequest();
  const { openLogin } = useLogin();

  const validationSchema = Yup.object().shape({
    recoveryCode: Yup.string().required().trim(),
    password: Yup.string()
      .test(
        'no-whitespace',
        intl.formatMessage({ id: 'fastReg.password.whitespaces.invalid' }),
        value => !(value && /\s/.test(value)),
      )
      .matches(/^\S{6,}$/, intl.formatMessage({ id: 'fastReg.password.invalid' }))
      .required(),
  });

  const successCallback = () => {
    triggerGTag(GTagEvents.forgot_password_sms_success);
    onSuccessCallback();
  };

  const errorCallback = () => {
    triggerGTag(GTagEvents.forgot_password_sms_error);
  };

  const handleSubmit = ({ recoveryCode, password }: { recoveryCode: string; password: string }) => {
    triggerGTag(GTagEvents.forgot_password_sms_submit);
    request({
      apiMethod: BffApiService.forgotPasswordSmsChange,
      payload: {
        mobile: mobile.mobile,
        mobilePrefix: mobile.mobilePrefix,
        recoveryCode,
        password,
      },
      successCallback,
      errorCallback,
    });
  };

  const openLoginClicked = () => {
    openLogin({ openedFrom: OpenedFrom.ForgotPasswordSms, variant: LoginFlowVariant.Mobile });
  };

  return (
    <>
      <div className="text-center text-white mb-4">
        {intl.formatMessage({ id: 'reset.sms.description' })}
      </div>
      <Form
        initialValues={{
          password: '',
          recoveryCode: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col w-full justify-between gap-5"
      >
        {() => (
          <>
            <TextInputFieldTransparent
              data-cy="forgot-password-sms-recovery"
              type="text"
              name="recoveryCode"
              label={intl.formatMessage({ id: 'inputs.code' })}
            />
            <PasswordFieldTransparent
              data-cy="forgot-password-sms-password"
              label={intl.formatMessage({
                id: 'inputs.password',
              })}
              name="password"
              onFocus={() => triggerGTag(GTagEvents.fast_reg_password_click)}
              onValidationError={error =>
                triggerGTag(GTagEvents.fast_reg_password_error, { error })
              }
              autoComplete="new-password"
            />
            <div className="flex flex-col items-center mt-4">
              {error && <PromptMessage type="error" message={error} />}
              <Button
                text={intl.formatMessage({ id: 'misc.submit' })}
                type="submit"
                btnType="primary"
                data-cy="forgot-password-sms-change-submit-button"
                isLoading={loading}
              />
              <div className="text-white text-[15px] text-center mt-6">
                <FormattedMessage
                  id="authentication.forgot-password.change-to-login"
                  values={{
                    a: str => (
                      <span
                        onClick={openLoginClicked}
                        className="text-blue-blue font-bold underline cursor-pointer"
                      >
                        {str}
                      </span>
                    ),
                  }}
                />
              </div>
              <p className="max-w-[80%] mt-3 block !text-[12px] italic text-white mb-0">
                {intl.formatMessage({ id: 'authentication.forgot-password-sms.description' })}
              </p>
            </div>
          </>
        )}
      </Form>
    </>
  );
};

export default ForgotPasswordSmsChangeForm;
